import api from './api';

// ------- Interfaces (FE, camelCase. Có fallback PascalCase vì STJ default ko bật camelCase policy) -------

export interface RuleDto {
  id: number;
  ruleName: string;
  sqlQuery: string;
  maloaitb: number;
  tenLoaiTb: string;
  mamucdo: number;
  tenMucDo: string;
  colorCode: string;
  active: number | null; // 1 = active, 0 = inactive
}

export interface LoaiTbDto {
  maloaitb: number;
  tenloaitb: string;
  mota: string;
}

export interface MucDoDto {
  mamucdo: number;
  tenmucdo: string;
  colorCode: string;
}

export interface CreateRuleRequest {
  ruleName: string;
  sqlQuery: string;
  maloaitb: number;
  mamucdo: number;
  active?: number;
}

export interface UpdateRuleRequest {
  ruleName: string;
  sqlQuery: string;
  maloaitb: number;
  mamucdo: number;
  active?: number;
}

export interface RunRuleResponse {
  success: boolean;
  ruleId: number;
  ruleName: string;
  rowsReturned: number;
  notificationsInserted: number;
  message: string;
}

export interface RunAllRulesResponse {
  success: boolean;
  rulesCount: number;
  totalRowsReturned: number;
  totalNotificationsInserted: number;
  message: string;
  details: RunRuleResponse[];
}

export interface CooldownInfoDto {
  canRun: boolean;
  lastRunTime: string | null;
  lastRunUser: string | null;
  remainingSeconds: number;
  cooldownMinutes: number;
}

export interface RuleRunHistoryDto {
  id: number;
  runTime: string;
  username: string;
  rulesCount: number;
  totalRowsReturned: number;
  totalNotificationsInserted: number;
  message: string;
}

export interface ConfigParamDto {
  id: number;
  paramKey: string;
  paramValue: string;
  description: string;
  updatedAt: string;
  updatedBy: string;
}

// ----------------- Helpers -----------------
const toNum = (a: any, def = 0) => {
  const v = Number(a ?? def);
  return Number.isFinite(v) ? v : def;
};
const toStr = (a: any, def = '') => ((a ?? def) === null || (a ?? def) === undefined ? def : String(a ?? def));
const toBool = (a: any, def = false) => {
  if (a === true || a === 1 || a === 'true' || a === '1') return true;
  if (a === false || a === 0 || a === 'false' || a === '0') return false;
  return def;
};

const mapRule = (r: any): RuleDto => ({
  id: toNum(r.id ?? r.Id, 0),
  ruleName: toStr(r.ruleName ?? r.RuleName),
  sqlQuery: toStr(r.sqlQuery ?? r.SqlQuery),
  maloaitb: toNum(r.maloaitb ?? r.Maloaitb, 0),
  tenLoaiTb: toStr(r.tenLoaiTb ?? r.TenLoaiTb),
  mamucdo: toNum(r.mamucdo ?? r.Mamucdo, 0),
  tenMucDo: toStr(r.tenMucDo ?? r.TenMucDo),
  colorCode: toStr(r.colorCode ?? r.ColorCode),
  active: (r.active ?? r.Active) === null || (r.active ?? r.Active) === undefined
    ? null
    : toNum(r.active ?? r.Active, 0)
});

const mapLoaiTb = (r: any): LoaiTbDto => ({
  maloaitb: toNum(r.maloaitb ?? r.Maloaitb, 0),
  tenloaitb: toStr(r.tenloaitb ?? r.Tenloaitb),
  mota: toStr(r.mota ?? r.Mota)
});

const mapMucDo = (r: any): MucDoDto => ({
  mamucdo: toNum(r.mamucdo ?? r.Mamucdo, 0),
  tenmucdo: toStr(r.tenmucdo ?? r.Tenmucdo),
  colorCode: toStr(r.colorCode ?? r.ColorCode)
});

const mapRunResponse = (d: any): RunRuleResponse => ({
  success: toBool(d.success ?? d.Success, false),
  ruleId: toNum(d.ruleId ?? d.RuleId, 0),
  ruleName: toStr(d.ruleName ?? d.RuleName),
  rowsReturned: toNum(d.rowsReturned ?? d.RowsReturned, 0),
  notificationsInserted: toNum(d.notificationsInserted ?? d.NotificationsInserted, 0),
  message: toStr(d.message ?? d.Message)
});

// ----------------- API -----------------
export const rulesService = {
  async getAll(): Promise<RuleDto[]> {
    const res = await api.get('/rules');
    return (res.data || []).map(mapRule);
  },

  async getById(id: number): Promise<RuleDto> {
    const res = await api.get(`/rules/${id}`);
    return mapRule(res.data);
  },

  async getLoaiTb(): Promise<LoaiTbDto[]> {
    const res = await api.get('/rules/loaitb');
    return (res.data || []).map(mapLoaiTb);
  },

  async getMucDo(): Promise<MucDoDto[]> {
    const res = await api.get('/rules/mucdo');
    return (res.data || []).map(mapMucDo);
  },

  async create(req: CreateRuleRequest): Promise<{ id: number; message: string }> {
    const res = await api.post('/rules', req);
    const d: any = res.data || {};
    return {
      id: toNum(d.id ?? d.Id, 0),
      message: toStr(d.message ?? d.Message)
    };
  },

  async update(id: number, req: UpdateRuleRequest): Promise<{ message: string }> {
    const res = await api.put(`/rules/${id}`, req);
    const d: any = res.data || {};
    return { message: toStr(d.message ?? d.Message) };
  },

  async remove(id: number): Promise<{ message: string }> {
    const res = await api.delete(`/rules/${id}`);
    const d: any = res.data || {};
    return { message: toStr(d.message ?? d.Message) };
  },

  async toggleActive(id: number, active: boolean): Promise<{ message: string; active: boolean }> {
    const res = await api.patch(`/rules/${id}/active`, { active });
    const d: any = res.data || {};
    return {
      message: toStr(d.message ?? d.Message),
      active: toBool(d.active ?? d.Active, active)
    };
  },

  async run(id: number): Promise<RunRuleResponse> {
    const res = await api.post(`/rules/${id}/run`);
    return mapRunResponse(res.data);
  },

  async runActive(): Promise<RunAllRulesResponse> {
    const res = await api.post('/rules/run-active');
    const d: any = res.data || {};
    const details: any[] = Array.isArray(d.details) ? d.details : (Array.isArray(d.Details) ? d.Details : []);
    return {
      success: toBool(d.success ?? d.Success, false),
      rulesCount: toNum(d.rulesCount ?? d.RulesCount, 0),
      totalRowsReturned: toNum(d.totalRowsReturned ?? d.TotalRowsReturned, 0),
      totalNotificationsInserted: toNum(d.totalNotificationsInserted ?? d.TotalNotificationsInserted, 0),
      message: toStr(d.message ?? d.Message),
      details: details.map(mapRunResponse)
    };
  },

  async getCooldownInfo(): Promise<CooldownInfoDto> {
    const res = await api.get('/rules/cooldown-info');
    const d: any = res.data || {};
    return {
      canRun: toBool(d.canRun ?? d.CanRun, true),
      lastRunTime: d.lastRunTime ?? d.LastRunTime ?? null,
      lastRunUser: d.lastRunUser ?? d.LastRunUser ?? null,
      remainingSeconds: toNum(d.remainingSeconds ?? d.RemainingSeconds, 0),
      cooldownMinutes: toNum(d.cooldownMinutes ?? d.CooldownMinutes, 10)
    };
  },

  async getLastRun(): Promise<RuleRunHistoryDto | null> {
    const res = await api.get('/rules/last-run');
    const d: any = res.data || {};
    if (!d.id && !d.Id) return null;
    return {
      id: toNum(d.id ?? d.Id, 0),
      runTime: toStr(d.runTime ?? d.RunTime),
      username: toStr(d.username ?? d.Username),
      rulesCount: toNum(d.rulesCount ?? d.RulesCount, 0),
      totalRowsReturned: toNum(d.totalRowsReturned ?? d.TotalRowsReturned, 0),
      totalNotificationsInserted: toNum(d.totalNotificationsInserted ?? d.TotalNotificationsInserted, 0),
      message: toStr(d.message ?? d.Message)
    };
  },

  async getConfig(paramKey: string): Promise<string | null> {
    const res = await api.get(`/rules/config/${paramKey}`);
    const d: any = res.data || {};
    return d.value ?? d.Value ?? null;
  },

  async upsertConfig(paramKey: string, paramValue: string, description?: string): Promise<{ message: string }> {
    const res = await api.post('/rules/config', { paramKey, paramValue, description });
    const d: any = res.data || {};
    return { message: toStr(d.message ?? d.Message) };
  }
};
